/**
 * User Tiers utilities.
 *
 * Tiers are stored in user.attributes.publicData.tier (string).
 * They can be set manually via admin Integration API or during
 * registration (e.g., 'basic' by default).
 *
 * Default tiers:
 *   'basic'    — free, standard user
 *   'verified' — identity-verified user (set by operator)
 *   'premium'  — paying member
 *   'pro'      — professional provider
 *
 * Additional custom tiers can be configured via ENV:
 *   REACT_APP_USER_TIERS — JSON array: '[{"key":"vip","label":"VIP","color":"#gold"}]'
 */

const DEFAULT_TIERS = [
  { key: 'basic', label: 'Basic', color: '#6b7280', bg: '#f3f4f6' },
  { key: 'verified', label: 'Verified', color: '#065f46', bg: '#d1fae5' },
  { key: 'premium', label: 'Premium', color: '#92400e', bg: '#fef3c7' },
  { key: 'pro', label: 'Pro', color: '#1e40af', bg: '#dbeafe' },
];

/**
 * Get tier config for a user.
 * @param {Object} user — Sharetribe user entity
 * @returns {Object|null}
 */
export const getUserTier = user => {
  const tierKey = user?.attributes?.publicData?.tier;
  if (!tierKey) return null;

  let customTiers = [];
  try {
    customTiers = JSON.parse(process.env.REACT_APP_USER_TIERS || '[]');
  } catch (_) {}

  const allTiers = [...DEFAULT_TIERS, ...customTiers];
  return allTiers.find(t => t.key === tierKey) || { key: tierKey, label: tierKey, color: '#6b7280', bg: '#f3f4f6' };
};

/**
 * Check if a user has a specific tier or higher.
 * Order: basic < verified < premium < pro
 * @param {Object} user
 * @param {string} requiredTier
 */
export const hasMinimumTier = (user, requiredTier) => {
  const TIER_RANKS = { basic: 0, verified: 1, premium: 2, pro: 3 };
  const userTierKey = user?.attributes?.publicData?.tier || 'basic';
  const userRank = TIER_RANKS[userTierKey] ?? 0;
  const requiredRank = TIER_RANKS[requiredTier] ?? 0;
  return userRank >= requiredRank;
};

/**
 * Get tier key from a user entity.
 */
export const getTierKey = user => user?.attributes?.publicData?.tier || null;
